import {Helmet} from 'react-helmet'

import { InformationCircleIcon } from "@heroicons/react/outline";
import { useState, useEffect } from "react";
import { Alert } from "./components/alerts/Alert";
import { Grid } from "./components/grid/Grid";
import { Keyboard } from "./components/keyboard/Keyboard";
import { InfoModal } from "./components/modals/InfoModal";
import { WinModal } from "./components/modals/WinModal";
import { isWordInWordList, isWinningWord, solution } from "./lib/words";
import "./App.css"
import Header from '../components/common/Header';


function App() {
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [isGameWon, setIsGameWon] = useState(false);
  const [isWinModalOpen, setIsWinModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isWordNotFoundAlertOpen, setIsWordNotFoundAlertOpen] = useState(false);
  const [isGameLost, setIsGameLost] = useState(false);

  useEffect(() => {
    if (isGameWon) {
      setIsWinModalOpen(true);
    }
  }, [isGameWon]);

  const onChar = (value: string) => {
    if (currentGuess.length < 5 && guesses.length < 6) {
      setCurrentGuess(`${currentGuess}${value}`);
    }
  };

  const onDelete = () => {
    setCurrentGuess(currentGuess.slice(0, -1));
  };

  const onEnter = () => {
    if (!isWordInWordList(currentGuess)) {
      setIsWordNotFoundAlertOpen(true);
      return setTimeout(() => {
        setIsWordNotFoundAlertOpen(false);
      }, 2000);
    }

    const winningWord = isWinningWord(currentGuess);

    if (currentGuess.length === 5 && guesses.length < 6 && !isGameWon) {
      setGuesses([...guesses, currentGuess]);
      setCurrentGuess("");

      if (winningWord) {
        return setIsGameWon(true);
      }

      if (guesses.length === 5) {
        setIsGameLost(true);
        return setTimeout(() => {
          setIsGameLost(false);
        }, 2000);
      }
    }
  };

  
  return (
    <div className="flex flex-col">
      <Helmet>
        {/* 页面标题 */}
        <title>Unlimited Spanish Wordle - Play Spanish Wordle Without Limits</title>
        <meta 
          name="description" 
          content="Play Unlimited Spanish Wordle and enjoy endless word guessing fun! No time limits, no daily restrictions—just unlimited word puzzles to challenge your vocabulary." 
        />
        <meta 
          name="keywords" 
          content="Unlimited Spanish Wordle, Spanish Wordle game, Spanish word puzzle, Spanish word guessing game, endless Spanish Wordle, Wordle alternatives" 
        />
        <meta name="author" content="GamesLikeWordle" />

        {/* Open Graph for Social Media */}
        <meta property="og:title" content="Unlimited Spanish Wordle - Play Spanish Wordle Without Limits" />
        <meta 
          property="og:description" 
          content="Enjoy unlimited Spanish Wordle games and challenge your vocabulary with endless word puzzles. Play as much as you want with no restrictions!" 
        />
        <meta property="og:url" content="https://www.gameslikewordle.com/spanish-wordle-unlimited" />
        <meta property="og:type" content="website" />
        {/* <meta property="og:image" content="https://www.gameslikewordle.com/assets/wordle-unlimited-og.jpg" /> */}

        {/* Twitter SEO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Unlimited Spanish Wordle - Play Spanish Wordle Without Limits" />
        <meta 
          name="twitter:description" 
          content="Dive into Unlimited Spanish Wordle and enjoy endless word guessing fun! No restrictions, no limits—just pure word puzzle excitement." 
        />
        <meta name="twitter:image" content="https://www.gameslikewordle.com/assets/spanish-wordle-unlimited-twitter.jpg" />
        <meta name="twitter:site" content="@GamesLikeWordle" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gameslikewordle.com/spanish-wordle-unlimited" />
      </Helmet>
      
      <Header></Header>


      <div className="flex w-80 mx-auto items-center my-4">
        <h1 className="text-xl grow font-bold">Wordle en español ilimitado</h1>
        <InformationCircleIcon
          className="h-6 w-6 cursor-pointer"
          onClick={() => setIsInfoModalOpen(true)}
        />
      </div>
      <div className="pt-2 px-1 pb-8 md:max-w-7xl w-full mx-auto sm:px-6 lg:px-8 flex flex-col grow">
        <div className="pb-6 grow">
          <Grid
            guesses={guesses}
            currentGuess={currentGuess}
          />
        </div>
        <Keyboard
          onChar={onChar}
          onDelete={onDelete}
          onEnter={onEnter}
          guesses={guesses}
        />
        <InfoModal
          isOpen={isInfoModalOpen}
          handleClose={() => setIsInfoModalOpen(false)}
        />
      </div>


      <Alert message="Palabra no encontrada" isOpen={isWordNotFoundAlertOpen} />
      <Alert
        message={`Perdiste, la palabra era ${solution}`}
        isOpen={isGameLost}
      />
      <WinModal
        isOpen={isWinModalOpen}
        handleClose={() => setIsWinModalOpen(false)}
        guesses={guesses}
      />


      <section className="intro-section mx-auto">
        <h1>What is Spanish Wordle?</h1>
      
        <p>Spanish Wordle is a Spanish version of the popular word game Wordle. The objective of the game is to guess a 5-letter word in 6 attempts. Each attempt is made with Spanish words, and the color of the squares indicates how close you are to guessing the correct word.</p>

        <h2>¿Qué es Wordle en español?</h2>
        <p>
          Wordle en español es una versión en español del popular juego de palabras Wordle. El objetivo del juego es adivinar una palabra de 5 letras en 6 intentos. Cada intento se realiza con palabras en español y el color de las casillas te indica cuán cerca estás de adivinar la palabra correcta.
        </p>
        <h2>¿Qué es Wordle?</h2>
        <p>
          Wordle es un juego web creado por el ingeniero de software galés Josh Wardle. El objetivo del juego es adivinar una palabra de 5 letras en 6 intentos. Cada intento da retroalimentación con colores que indican si las letras son correctas o están en la posición correcta.
        </p>
        <p>
          Wordle fue adquirido por The New York Times en 2022 y es jugado por millones de personas en todo el mundo.
        </p>
        <h2>Juegos Similares</h2>
        <ul>
          <li><a href="../dictadle" target="_blank"><strong>Dictadle</strong></a>: Un juego donde escuchas un clip de audio y tratas de adivinar la frase. Tienes 5 intentos y cada intento te dará retroalimentación con colores.</li>
          <li><a href="../wordle-unlimited" target="_blank"><strong>Wordle Unlimited</strong></a>: La versión ilimitada de Wordle, donde puedes jugar tantas veces como quieras sin restricciones de tiempo ni de intentos.</li>
        </ul>
      </section>
    </div>
  )
}

export default App;
